import { isString, isUndefined } from '#library/assertion.library.js';
import { secure } from '#library/proxy.library.js';
/**
 * ## BoundedCache
 * High-performance Map implementation enforcing maximum capacity (LRU) and TTL eviction.
 * Supports static keys (immortal glossary definitions) and date-salt staleness purging.
 */
export class BoundedCache extends Map {
    #timestamps = new Map();
    #staticKeys = new Set();
    maxSize;
    ttl;
    constructor(maxSize = 1000, ttl = 24 * 60 * 60 * 1000) {
        super();
        this.maxSize = maxSize;
        this.ttl = ttl;
    }
    get isBoundedCache() {
        return true;
    }
    #isExpired(key) {
        if (this.#staticKeys.has(key))
            return false;
        const time = this.#timestamps.get(key);
        if (isUndefined(time))
            return false;
        return Date.now() - time > this.ttl;
    }
    /**
     * Purges TTL-expired entries.
     */
    evictExpired() {
        const now = Date.now();
        for (const key of super.keys()) {
            if (this.#staticKeys.has(key))
                continue;
            const time = this.#timestamps.get(key);
            if (time !== undefined && (now - time > this.ttl)) {
                this.delete(key);
            }
        }
    }
    /**
     * Register an immortal static key (e.g. business glossary term).
     * Static keys bypass TTL expiration and LRU capacity eviction.
     */
    setStatic(key, value) {
        if (super.has(key))
            super.delete(key);
        super.set(key, value);
        this.#timestamps.delete(key);
        this.#staticKeys.add(key);
        return this;
    }
    /**
     * Check if a key is a static key.
     */
    isStatic(key) {
        return this.#staticKeys.has(key);
    }
    get(key) {
        if (this.#isExpired(key)) {
            this.delete(key);
            return undefined;
        }
        if (!super.has(key))
            return undefined;
        const val = super.get(key);
        super.delete(key);
        super.set(key, val);
        if (!this.#staticKeys.has(key))
            this.#timestamps.set(key, Date.now());
        return val;
    }
    has(key) {
        if (this.#isExpired(key)) {
            this.delete(key);
            return false;
        }
        return super.has(key);
    }
    get size() {
        this.evictExpired();
        return super.size;
    }
    forEach(callbackfn, thisArg) {
        this.evictExpired();
        super.forEach(callbackfn, thisArg);
    }
    set(key, value) {
        this.evictExpired();
        if (super.has(key))
            super.delete(key);
        super.set(key, value);
        this.#staticKeys.delete(key);
        this.#timestamps.set(key, Date.now());
        while (this.size > this.maxSize) {
            let evicted = false;
            const keysIter = super.keys();
            let res = keysIter.next();
            while (!res.done) {
                const k = res.value;
                if (!this.#staticKeys.has(k)) {
                    this.delete(k);
                    evicted = true;
                    break;
                }
                res = keysIter.next();
            }
            if (!evicted)
                break;
        }
        return this;
    }
    delete(key) {
        this.#timestamps.delete(key);
        this.#staticKeys.delete(key);
        return super.delete(key);
    }
    deletePrefix(prefix) {
        const normalizedPrefix = String(prefix).trim().toLowerCase();
        const toDelete = [];
        for (const key of super.keys()) {
            if (isString(key) && key.toLowerCase().startsWith(normalizedPrefix))
                toDelete.push(key);
        }
        for (const k of toDelete)
            this.delete(k);
        return toDelete.length;
    }
    /**
     * Purge cache entries.
     * If `count` is specified, evicts up to `count` oldest non-static entries.
     * If omitted, clears all entries, including static entries.
     */
    clear(count) {
        if (count === undefined) {
            this.#timestamps.clear();
            this.#staticKeys.clear();
            super.clear();
            return;
        }
        let evicted = 0;
        for (const key of super.keys()) {
            if (evicted >= count)
                break;
            if (!this.#staticKeys.has(key)) {
                this.delete(key);
                evicted++;
            }
        }
    }
    keys() {
        this.evictExpired();
        return super.keys();
    }
    values() {
        this.evictExpired();
        return super.values();
    }
    entries() {
        this.evictExpired();
        return super.entries();
    }
    [Symbol.iterator]() {
        this.evictExpired();
        return super[Symbol.iterator]();
    }
    /**
     * Returns a plain key-value object of all active non-expired cache entries.
     * Filters out non-string keys to prevent lossy key conversions or collisions (e.g. numeric 1 vs string "1").
     */
    toJSON() {
        this.evictExpired();
        const stringEntries = [];
        for (const [k, v] of super.entries()) {
            if (typeof k === 'string')
                stringEntries.push([k, v]);
        }
        return Object.fromEntries(stringEntries);
    }
    static fromEntries(entries, maxSize = 1000, ttl = 24 * 60 * 60 * 1000) {
        const cache = new BoundedCache(maxSize, ttl);
        for (const [k, v] of entries) {
            cache.set(k, v);
        }
        return cache;
    }
}
/**
 * Creates a normalized cache facade exposing safe operations over the active state's BoundedCache.
 */
export function createCacheFacade(getState) {
    return secure({
        get(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().cache.get(normalized);
        },
        has(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().cache.has(normalized);
        },
        set(key, value) {
            const normalized = String(key).trim().toLowerCase();
            getState().cache.set(normalized, String(value));
            return this;
        },
        setStatic(key, value) {
            const normalized = String(key).trim().toLowerCase();
            getState().cache.setStatic(normalized, String(value));
            return this;
        },
        delete(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().cache.delete(normalized);
        },
        deletePrefix(prefix) {
            const normalizedPrefix = String(prefix).trim().toLowerCase();
            return getState().cache.deletePrefix(normalizedPrefix);
        },
        clear(count) {
            getState().cache.clear(count);
        },
        entries() {
            return getState().cache.entries();
        },
        fromEntries(entries) {
            for (const [k, v] of entries) {
                const normalized = String(k).trim().toLowerCase();
                getState().cache.set(normalized, String(v));
            }
            return this;
        },
        toJSON() {
            return getState().cache.toJSON();
        }
    });
}
